'use client';

import React, { useState } from 'react';

const partnerTypes = [
  'Family Office',
  'Venture Fund',
  'Angel Network',
  'Accelerator',
  'Corporate',
  'University',
  'Startup Ecosystem',
  'Investment Professional',
  'Strategic Partner',
  'Other',
];

export default function PartnersForm() {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    organisation: '',
    partnerType: '',
    website: '',
    description: '',
    message: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bento-card rounded-3xl p-12 text-center">
        <div className="w-16 h-16 rounded-full border border-primary/40 bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-primary"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-3">Message Received</h3>
        <p className="text-sm text-muted-foreground font-light leading-relaxed max-w-md mx-auto">
          Thank you for reaching out about a potential partnership. We will review your message and
          be in touch shortly.
        </p>
      </div>
    );
  }

  return (
    <div className="bento-card rounded-3xl p-8 sm:p-10">
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-foreground mb-2">Work with Aregn</h2>
        <p className="text-sm text-muted-foreground font-light mb-6">
          We welcome conversations with partners across the investment and startup ecosystem.
        </p>

        {/* Partner type tags */}
        <div className="flex flex-wrap gap-2">
          {partnerTypes.map((type) => (
            <span
              key={type}
              className="text-xs px-3 py-1.5 rounded-full border border-primary/15 text-muted-foreground"
            >
              {type}
            </span>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Full Name *
            </label>
            <input
              type="text"
              name="fullName"
              required
              value={formData.fullName}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="Your full name"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Email *
            </label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="your@email.com"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Organisation *
            </label>
            <input
              type="text"
              name="organisation"
              required
              value={formData.organisation}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="Organisation name"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Partner Type
            </label>
            <select
              name="partnerType"
              value={formData.partnerType}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors"
            >
              <option value="">Select type</option>
              {partnerTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
            Website
          </label>
          <input
            type="url"
            name="website"
            value={formData.website}
            onChange={handleChange}
            className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
            placeholder="https://yourorganisation.com"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
            Brief Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors resize-none"
            placeholder="Tell us about your organisation and what you do..."
          />
        </div>

        <div>
          <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
            How Would You Like to Collaborate?
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors resize-none"
            placeholder="Describe how you see a potential collaboration with Aregn Capital..."
          />
        </div>

        <button
          type="submit"
          className="w-full py-4 rounded-full bg-primary text-primary-foreground text-sm font-semibold tracking-widest uppercase hover:bg-accent transition-all duration-300 hover:scale-[1.02] active:scale-100"
        >
          Explore a Partnership
        </button>
      </form>
    </div>
  );
}
